
import React, { useState } from "react";
import { useTodosLive } from "../../hooks/useTodosLive";
import { downloadJson } from "../../utils/download";

interface Props {
    onNavigate?: (key: string, id: string) => void;
}

export function DashboardTodosLiveDebugTab({ onNavigate }: Props) {
    const { 
        liveStatus, 
        liveMsg, 
        rawTodos,
        summary, 
        meta,
        lastSnapshotAt,
        startListener, 
        stopListener,
        reconnect
    } = useTodosLive();

    const [isBackfilling, setIsBackfilling] = useState(false);
    const [filterUser, setFilterUser] = useState<string>('ALL');

    const handleBackfill = async () => {
        if (!confirm("確定重建索引？這將從 ApprovalFlows 重新計算 Todos。")) return;
        setIsBackfilling(true);
        try {
            const { functions } = await import("../../../firebaseClient");
            const fn = functions.httpsCallable('runBackfillTodos');
            const res = await fn({ limit: 1000 });
            alert(`Backfill Complete: ${JSON.stringify(res.data)}`);
            reconnect();
        } catch (e: any) {
            alert("Error: " + e.message);
        } finally {
            setIsBackfilling(false);
        }
    };

    const handleJumpToApp = (refId: string, module: string) => {
        if (!onNavigate || !refId) return alert(`無法跳轉: ID=${refId}`);
        let navKey = 'dashboard';
        const m = (module||"").toUpperCase();
        if (m.includes('CONTRACT') || m.includes('REVIEW')) navKey = 'contractReviews';
        else if (m.includes('SEAL')) navKey = 'sealApplication';
        else if (m.includes('COMPLIANCE') || m.includes('GOV')) navKey = 'complianceControlCenter';
        else if (m.includes('CASE')) navKey = 'legalMatters';
        
        onNavigate(navKey, refId);
    };

    const filteredTodos = rawTodos.filter(t => filterUser === 'ALL' || t.assigneeUserKey === filterUser);

    return (
        <div style={{display:'flex', flexDirection:'column', gap:24, width:'100%'}}>
            {/* A. Listener Status */}
            <div className="section">
                <h3 className="h1" style={{color:'#60a5fa'}}>A. 待辦監控 (Source: Todos Index)</h3>
                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10}}>
                    <div style={{background:'#222', padding:10, borderRadius:6}}>
                        Status: <b>{liveStatus}</b> <br/>
                        Last Update: {lastSnapshotAt ? lastSnapshotAt.toLocaleTimeString() : '-'}
                    </div>
                    <div style={{background:'#222', padding:10, borderRadius:6}}>
                         Active Count: <b>{rawTodos.length}</b> <br/>
                         Unknown Count: <b style={{color: (meta?.unknownCount || 0) > 0 ? 'var(--danger)' : 'var(--ok)'}}>{meta?.unknownCount || 0}</b>
                    </div>
                </div>
                <div style={{display:'flex', gap:10}}>
                    <button className="smallbtn" onClick={reconnect}>Reconnect</button>
                    <button className="smallbtn" onClick={startListener}>Start</button>
                    <button className="smallbtn" onClick={stopListener} style={{color:'var(--danger)', borderColor:'var(--danger)'}}>Stop</button>
                    <button className="smallbtn" onClick={handleBackfill} disabled={isBackfilling} style={{background:'#a5b4fc', color:'#000'}}>
                        {isBackfilling ? "Rebuilding..." : "重建索引 (Admin Only)"}
                    </button>
                    <button className="smallbtn" onClick={() => downloadJson('todos_dump.json', rawTodos)}>Download JSON</button>
                </div>
            </div>

            {/* B. Stats Visualization */}
            <div className="section" style={{border: '1px solid #a78bfa'}}>
                <h3 className="h1" style={{color:'#a78bfa', borderBottom:'1px solid #444', paddingBottom:10, marginBottom:16}}>B. 儀錶板歸屬統計</h3>
                <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:12}}>
                    <StatCard user="KEVIN" counts={summary?.byUser?.kevin?.counts} color="#fbbf24" />
                    <StatCard user="LISA" counts={summary?.byUser?.lisa?.counts} color="#a78bfa" />
                    <StatCard user="IRENE" counts={summary?.byUser?.irene?.counts} color="#34d399" />
                </div>
            </div>

            {/* C. Unknowns Diagnostics */}
            <div className="section" style={{border: '1px solid #fbbf24'}}>
                <h3 className="h1" style={{color: '#fbbf24', marginBottom: 12}}>C. Unknowns (無法歸屬)</h3>
                {!summary?.unknown || summary.unknown.length === 0 ? (
                    <div style={{color:'var(--ok)', padding:10, background:'rgba(16, 185, 129, 0.1)', borderRadius:6}}>✅ All Clean. No unknowns.</div>
                ) : (
                    <div style={{background:'#111', padding:10, borderRadius:8, border:'1px solid #333', overflowX:'auto'}}>
                        <table style={{width:'100%', borderCollapse:'collapse', fontSize:12, fontFamily:'monospace'}}>
                            <thead>
                                <tr style={{textAlign:'left', color:'#888', background:'#222'}}>
                                    <th style={{padding:8}}>Module</th>
                                    <th style={{padding:8}}>Title / ID</th>
                                    <th style={{padding:8}}>Stage Key</th>
                                    <th style={{padding:8}}>Reason</th>
                                    <th style={{padding:8}}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {summary.unknown.map((t, i) => (
                                    <tr key={t.id} style={{borderBottom:'1px solid #333'}}>
                                        <td style={{padding:8}}>{t.module}</td>
                                        <td style={{padding:8}}><div style={{color:'#fff'}}>{t.title}</div><div style={{color:'#666', fontSize:10}}>{t.id}</div></td>
                                        <td style={{padding:8, color:'#a5b4fc'}}>{t.currentStageKey || "N/A"}</td>
                                        <td style={{padding:8, color:'#f87171'}}>{t._derivationReason || "Missing"}</td>
                                        <td style={{padding:8}}><button className="smallbtn" onClick={() => handleJumpToApp(t.entityId || t.id, t.module)}>Go</button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* D. Full List */}
            <div className="section">
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12}}>
                    <h3 className="h1" style={{color: '#34d399', margin:0}}>D. 全部 Active Todos ({filteredTodos.length})</h3>
                    <select className="input" style={{width: 150}} value={filterUser} onChange={e => setFilterUser(e.target.value)}>
                        <option value="ALL">All Users</option>
                        <option value="KEVIN">Kevin Only</option>
                        <option value="LISA">Lisa Only</option>
                        <option value="IRENE">Irene Only</option>
                    </select>
                </div>
                <div style={{maxHeight: 500, overflowY:'auto'}}>
                    <table style={{width:'100%', borderCollapse:'collapse', fontSize:12}}>
                         <thead>
                             <tr style={{textAlign:'left', background:'#222', color:'#ccc'}}>
                                 <th style={{padding:8}}>User</th>
                                 <th style={{padding:8}}>Bucket</th>
                                 <th style={{padding:8}}>Title</th>
                                 <th style={{padding:8}}>Stage</th>
                                 <th style={{padding:8}}>Link</th>
                             </tr>
                         </thead>
                         <tbody>
                             {filteredTodos.map(t => (
                                 <tr key={t.id} style={{borderBottom:'1px solid #333'}}>
                                     <td style={{padding:8, fontWeight:'bold', color: t.assigneeUserKey==='KEVIN'?'#fbbf24':t.assigneeUserKey==='LISA'?'#a78bfa':'#34d399'}}>{t.assigneeUserKey}</td>
                                     <td style={{padding:8}}>{t.dashboardBucket}</td>
                                     <td style={{padding:8}}>{t.title}</td>
                                     <td style={{padding:8, color:'#aaa'}}>{t.currentStageKey}</td>
                                     <td style={{padding:8}}><button className="smallbtn" onClick={() => handleJumpToApp(t.entityId||t.id, t.module)}>View</button></td>
                                 </tr>
                             ))}
                         </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function StatCard({user, counts, color}: any) {
    if (!counts) return <div style={{padding:12}}>Loading...</div>;
    return (
        <div style={{background:'rgba(255,255,255,0.05)', padding:12, borderRadius:8, borderLeft:`4px solid ${color}`}}>
            <div style={{fontWeight:'bold', color:color, marginBottom:6, display:'flex', justifyContent:'space-between'}}>
                {user} <span style={{fontSize:18}}>{counts.total}</span>
            </div>
            <div style={{display:'flex', flexDirection:'column', gap: 4, fontSize:12, color:'#ccc'}}>
                 <div style={{display:'flex', justifyContent:'space-between'}}><span>Contracts</span> <b>{counts.contracts}</b></div>
                 <div style={{display:'flex', justifyContent:'space-between'}}><span>Seals</span> <b>{counts.seals}</b></div>
                 <div style={{display:'flex', justifyContent:'space-between'}}><span>Compliance</span> <b>{counts.compliance}</b></div>
                 <div style={{display:'flex', justifyContent:'space-between'}}><span>Governance</span> <b>{counts.governance}</b></div>
                 <div style={{display:'flex', justifyContent:'space-between'}}><span>Cases</span> <b>{counts.cases}</b></div>
            </div>
        </div>
    );
}
